import React, { useEffect, useState, useMemo } from 'react';
import { Plus, CheckCircle, Clock, ChevronDown, ChevronRight, FolderOpen, CalendarCheck } from 'lucide-react';
import Layout from '../components/Layout';
import { workflowAPI } from '../utils/api';
import { toast } from 'sonner';

const DataLog = ({ embedded }) => {
  const [uploads, setUploads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    event_id: '',
    crew_member_name: '',
    upload_date: new Date().toISOString().split('T')[0],
    delivery_method: 'In Person',
    file_links: '',
    notes: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [uploadsRes, projectsRes, eventsRes] = await Promise.all([
        workflowAPI.getCrewUploads(),
        workflowAPI.getProjects(),
        workflowAPI.getEvents()
      ]);
      setUploads(uploadsRes.data);
      setProjects(projectsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        file_links: formData.file_links.split('\n').filter(link => link.trim())
      };
      await workflowAPI.createCrewUpload(submitData);
      setShowForm(false);
      setFormData({ project_id: '', event_id: '', crew_member_name: '', upload_date: new Date().toISOString().split('T')[0], delivery_method: 'In Person', file_links: '', notes: '' });
      toast.success('Data receipt logged');
      fetchData();
    } catch (error) {
      console.error('Error creating upload:', error);
      toast.error('Error logging receipt');
    }
  };

  const handleVerify = async (uploadId) => {
    try {
      await workflowAPI.verifyUpload(uploadId);
      toast.success('Verified');
      fetchData();
    } catch (error) {
      console.error('Error verifying:', error);
    }
  };

  const handleUnverify = async (uploadId) => {
    try {
      await workflowAPI.unverifyUpload(uploadId);
      fetchData();
    } catch (error) {
      console.error('Error unverifying:', error);
    }
  };

  const handleUpdateNotes = async (uploadId, notes) => {
    try {
      await workflowAPI.updateUploadNotes(uploadId, notes);
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const handleUpdateDeliveryMethod = async (uploadId, method) => {
    try {
      await workflowAPI.updateDeliveryMethod(uploadId, method);
      fetchData();
    } catch (error) {
      console.error('Error updating delivery method:', error);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? `${project.project_code} - ${project.bride_name} & ${project.groom_name}` : 'Unknown';
  };

  const getProjectCode = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.project_code : 'Unknown';
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.event_type : 'Unknown';
  };

  const filteredEvents = formData.project_id ? events.filter(e => e.project_id === formData.project_id) : events;

  // Group uploads: Project -> Event -> Uploads
  const groupedData = useMemo(() => {
    const grouped = {};
    uploads.forEach(upload => {
      if (!grouped[upload.project_id]) {
        grouped[upload.project_id] = {};
      }
      if (!grouped[upload.project_id][upload.event_id]) {
        grouped[upload.project_id][upload.event_id] = [];
      }
      grouped[upload.project_id][upload.event_id].push(upload);
    });

    // Sort projects by the project list order, put projects with uploads first
    const projectOrder = projects.map(p => p.id);
    const sortedProjectIds = Object.keys(grouped).sort((a, b) => {
      return projectOrder.indexOf(a) - projectOrder.indexOf(b);
    });

    return sortedProjectIds.map(projectId => {
      const project = projects.find(p => p.id === projectId);
      const eventGroups = Object.entries(grouped[projectId]).map(([eventId, eventUploads]) => {
        const event = events.find(e => e.id === eventId);
        return { eventId, event, uploads: eventUploads };
      });
      const totalUploads = eventGroups.reduce((sum, eg) => sum + eg.uploads.length, 0);
      const verifiedCount = eventGroups.reduce((sum, eg) => sum + eg.uploads.filter(u => u.admin_verified === 'Y').length, 0);
      return { projectId, project, eventGroups, totalUploads, verifiedCount };
    });
  }, [uploads, projects, events]);

  const Wrap = embedded ? React.Fragment : Layout;

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-muted-foreground font-serif">Loading...</div>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="p-6 lg:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-serif tracking-tight" data-testid="data-log-title">Data Log</h1>
            <p className="text-muted-foreground mt-1">Track when footage/data is received</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium"
            data-testid="log-receipt-button"
          >
            <Plus className="h-5 w-5" />
            {showForm ? 'Cancel' : 'Log Receipt'}
          </button>
        </div>

        {showForm && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="text-xl font-serif mb-4">Log Data Receipt</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Project *</label>
                  <select required value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value, event_id: '' })} className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="select-log-project">
                    <option value="">Select Project</option>
                    {projects.map(p => (<option key={p.id} value={p.id}>{p.project_code} - {p.bride_name} & {p.groom_name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Event *</label>
                  <select required value={formData.event_id} onChange={(e) => setFormData({ ...formData, event_id: e.target.value })} className="w-full px-4 py-3 border border-input bg-background text-sm" disabled={!formData.project_id} data-testid="select-log-event">
                    <option value="">Select Event</option>
                    {filteredEvents.map(e => (<option key={e.id} value={e.id}>{e.event_type}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Person/Crew Member *</label>
                  <input type="text" required value={formData.crew_member_name} onChange={(e) => setFormData({ ...formData, crew_member_name: e.target.value })} placeholder="Who delivered the data?" className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="crew-member-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Date Received *</label>
                  <input type="date" required value={formData.upload_date} onChange={(e) => setFormData({ ...formData, upload_date: e.target.value })} className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="upload-date-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Delivery Method *</label>
                  <select required value={formData.delivery_method} onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })} className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="delivery-method-select">
                    <option value="In Person">In Person</option>
                    <option value="Online Upload">Online Upload</option>
                    <option value="Cloud Link">Cloud Link</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide mb-1">File Links (optional, one per line)</label>
                <textarea rows="3" value={formData.file_links} onChange={(e) => setFormData({ ...formData, file_links: e.target.value })} placeholder="https://drive.google.com/..." className="w-full px-4 py-3 border border-input bg-background text-sm" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide mb-1">Notes</label>
                <input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional details..." className="w-full px-4 py-3 border border-input bg-background text-sm" />
              </div>
              <button type="submit" className="w-full px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium" data-testid="submit-log">
                Log Receipt
              </button>
            </form>
          </div>
        )}

        <div className="space-y-6">
          {uploads.length === 0 ? (
            <div className="bg-card border border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">No data receipts logged yet</p>
              <button onClick={() => setShowForm(true)} className="text-gold font-medium hover:underline">Log your first receipt</button>
            </div>
          ) : (
            groupedData.map(({ projectId, project, eventGroups, totalUploads, verifiedCount }) => (
              <ProjectGroup
                key={projectId}
                project={project}
                projectCode={getProjectCode(projectId)}
                projectName={getProjectName(projectId)}
                eventGroups={eventGroups}
                totalUploads={totalUploads}
                verifiedCount={verifiedCount}
                onVerify={handleVerify}
                onUnverify={handleUnverify}
                onUpdateNotes={handleUpdateNotes}
                onUpdateDeliveryMethod={handleUpdateDeliveryMethod}
              />
            ))
          )}
        </div>
      </div>
    </Wrap>
  );
};

const ProjectGroup = ({ project, projectCode, projectName, eventGroups, totalUploads, verifiedCount, onVerify, onUnverify, onUpdateNotes, onUpdateDeliveryMethod }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-border bg-card" data-testid="project-group">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-accent/50 transition-colors text-left"
        data-testid="project-group-toggle"
      >
        <div className="flex items-center gap-3">
          <FolderOpen className="h-5 w-5 text-gold flex-shrink-0" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-gold bg-gold/10 px-2 py-0.5">{projectCode}</span>
              <h2 className="text-lg font-serif">{project ? `${project.bride_name} & ${project.groom_name}` : 'Unknown Project'}</h2>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalUploads} receipt{totalUploads !== 1 ? 's' : ''} &middot; {verifiedCount} verified &middot; {eventGroups.length} event{eventGroups.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-0.5 text-xs font-medium ${verifiedCount === totalUploads ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'}`}>
            {verifiedCount}/{totalUploads} verified
          </span>
          {expanded ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border">
          {eventGroups.map(({ eventId, event, uploads }) => {
            const eventVerified = uploads.filter(u => u.admin_verified === 'Y').length;
            const allVerified = uploads.length > 0 && eventVerified === uploads.length;
            return (
            <div key={eventId} className="border-b border-border last:border-b-0" data-testid="event-group">
              <div className="flex items-center gap-2 px-5 py-3 bg-accent/30">
                <CalendarCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{event ? event.event_type : 'Unknown Event'}</span>
                {event?.event_date && <span className="text-xs text-muted-foreground">&middot; {event.event_date}</span>}
                <div className="flex items-center gap-2 ml-auto">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${allVerified ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'}`}>
                    {allVerified ? 'All Data Collected' : `${eventVerified}/${uploads.length} verified`}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-border">
                {uploads.map(upload => (
                  <UploadCard
                    key={upload.id}
                    upload={upload}
                    onVerify={onVerify}
                    onUnverify={onUnverify}
                    onUpdateNotes={onUpdateNotes}
                    onUpdateDeliveryMethod={onUpdateDeliveryMethod}
                  />
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const UploadCard = ({ upload, onVerify, onUnverify, onUpdateNotes, onUpdateDeliveryMethod }) => (
  <div className="px-5 py-4 hover:bg-accent/20 transition-colors" data-testid="data-log-entry">
    <div className="flex items-start gap-3">
      <div className={`flex-shrink-0 w-9 h-9 flex items-center justify-center mt-0.5 ${upload.admin_verified === 'Y' ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'bg-amber-100 dark:bg-amber-500/15'}`}>
        {upload.admin_verified === 'Y' ? <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <p className="text-sm font-medium">Delivered by {upload.crew_member_name}</p>
            <p className="text-xs text-muted-foreground">{upload.upload_date}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={upload.delivery_method || 'In Person'}
              onChange={(e) => onUpdateDeliveryMethod(upload.id, e.target.value)}
              className="px-2 py-1 text-xs border border-input bg-background"
            >
              <option value="In Person">In Person</option>
              <option value="Online Upload">Online Upload</option>
              <option value="Cloud Link">Cloud Link</option>
            </select>
            <span className={`px-2 py-0.5 text-xs font-medium whitespace-nowrap ${upload.admin_verified === 'Y' ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300' : 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300'}`}>
              {upload.admin_verified === 'Y' ? 'Verified' : 'Pending'}
            </span>
          </div>
        </div>

        <div className="mt-2">
          <textarea
            value={upload.notes || ''}
            onChange={(e) => onUpdateNotes(upload.id, e.target.value)}
            placeholder="Add notes..."
            rows="1"
            className="w-full px-3 py-1.5 border border-input bg-background text-xs"
          />
        </div>

        {upload.file_links && upload.file_links.length > 0 && (
          <div className="mt-2 flex gap-2 flex-wrap">
            {upload.file_links.map((link, idx) => (
              <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-gold hover:underline">File {idx + 1}</a>
            ))}
          </div>
        )}

        <div className="mt-2">
          {upload.admin_verified === 'N' && (
            <button onClick={() => onVerify(upload.id)} className="px-3 py-1.5 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-xs font-medium" data-testid="verify-button">
              Mark as Verified
            </button>
          )}
          {upload.admin_verified === 'Y' && (
            <div className="flex items-center gap-3">
              {upload.verified_date && <span className="text-xs text-muted-foreground">Verified on {upload.verified_date}</span>}
              <button onClick={() => onUnverify(upload.id)} className="px-3 py-1.5 bg-accent text-foreground hover:bg-accent/80 transition-all text-xs font-medium" data-testid="unverify-button">
                Undo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default DataLog;
